import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { MessageCircle, ChevronDown, Send, Circle } from "lucide-react";
import { projectMessages, sendProjectMessage } from "../lib/projects";

const hhmm = (iso) =>
  new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export const SupportChatPanel = ({ project, bioedilOnline, unread, onRead }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    if (!project) return;
    try {
      const res = await projectMessages(project.id, project.token);
      setMessages(res.messages);
      onRead?.();
    } catch {
      /* silenzioso */
    }
  };

  useEffect(() => {
    if (!open || !project) return;
    load();
    const iv = setInterval(() => {
      if (!document.hidden) load();
    }, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages, open]);

  const send = async () => {
    const value = text.trim();
    if (!value || !project) return;
    setBusy(true);
    try {
      const msg = await sendProjectMessage(project.id, project.token, value);
      setMessages((m) => [...m, msg]);
      setText("");
    } catch {
      toast.error("Messaggio non inviato, riprova");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card" data-testid="support-chat-panel">
      <button
        data-testid="support-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#F1F5F9]"
      >
        <span className="flex items-center gap-3">
          <MessageCircle className="h-4 w-4 flex-none text-[#4E7C59]" />
          <span>
            <span className="block label-xs text-[#4E7C59]">Assistenza BioEdil</span>
            <span className="block font-display text-base font-medium">
              Hai un dubbio mentre configuri il tuo progetto? Scrivici qui.
            </span>
          </span>
        </span>
        <span className="flex items-center gap-3">
          {unread > 0 && (
            <span
              data-testid="support-chat-unread"
              className="bg-[#4E7C59] px-2 py-0.5 label-xs text-white"
            >
              {unread} nuovo{unread > 1 ? "i" : ""} da BioEdil
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 flex-none text-[#4E7C59] transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
          />
        </span>
      </button>

      {open && (
        <div className="border-t border-border px-5 py-4">
          <div
            data-testid="support-chat-status"
            className="mb-3 flex items-center gap-2 label-xs text-muted-foreground"
          >
            <Circle
              className={`h-2.5 w-2.5 ${bioedilOnline ? "fill-[#4E7C59] text-[#4E7C59]" : "fill-[#A8A29E] text-[#A8A29E]"}`}
            />
            {bioedilOnline
              ? "BioEdil è disponibile"
              : "Puoi lasciarci un messaggio. Ti risponderemo appena possibile."}
          </div>

          <div
            data-testid="support-chat-messages"
            className="max-h-[280px] space-y-2 overflow-y-auto border border-border bg-[#F8FAFC] p-3"
          >
            {messages.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nessun messaggio. Scrivi la tua domanda: vedremo la tua configurazione e ti
                risponderemo qui.
              </p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[85%] border px-3 py-2 text-sm ${
                    m.sender === "cliente"
                      ? "ml-auto border-[#4E7C59] bg-[#4E7C59]/[0.08]"
                      : "border-border bg-card"
                  }`}
                >
                  <div className="label-xs mb-0.5 text-muted-foreground">
                    {m.sender === "cliente" ? "Tu" : "BioEdil"} · {hhmm(m.created_at)}
                  </div>
                  {m.text}
                </div>
              ))
            )}
            <div ref={endRef} />
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
            <Textarea
              data-testid="support-chat-input"
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Scrivi qui la tua domanda…"
              className="flex-1 rounded-none"
            />
            <Button
              data-testid="support-chat-send"
              onClick={send}
              disabled={busy || !text.trim()}
              className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              <Send className="mr-2 h-4 w-4" /> Invia
            </Button>
          </div>
          <p className="mt-2 label-xs text-muted-foreground">
            Nessun dato personale richiesto: vediamo solo il progetto che stai configurando.
          </p>
        </div>
      )}
    </div>
  );
};
